import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api, apiUpload } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { BadgeCheck, Radio, Edit3, Save, X, Upload, CalendarPlus, Trash2, ExternalLink, Zap, ZapOff } from "lucide-react";

export default function Dossier() {
  const { id } = useParams();
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [events, setEvents] = useState([]);
  const [evOpen, setEvOpen] = useState(false);
  const [evForm, setEvForm] = useState({ game: "Hell Let Loose", scheduled_at: "", stream_url: "", title: "", note: "" });
  const fileRef = useRef(null);

  const load = () => api.get(`/streamers/${id}`).then((r) => { setS(r.data); setForm(r.data); });
  const loadEvents = () => api.get(`/streamers/${id}/events`).then((r) => setEvents(r.data)).catch(() => {});
  useEffect(() => { load(); loadEvents(); }, [id]);

  const uploadAvatar = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Pick an image file."); return; }
    if (f.size > 3 * 1024 * 1024) { toast.error("Image too large (max 3 MB)."); return; }
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await apiUpload(`/streamers/${id}/avatar`, fd);
      setS((prev) => ({ ...prev, avatar_url: data.avatar_url }));
      setForm((prev) => ({ ...prev, avatar_url: data.avatar_url }));
      toast.success("Profile picture updated.");
    } catch (er) {
      toast.error(er.response?.data?.detail || "Upload failed");
    }
  };

  const submitEvent = async () => {
    if (!evForm.scheduled_at || !evForm.stream_url) { toast.error("Kickoff and stream URL are required."); return; }
    try {
      const payload = { ...evForm, scheduled_at: new Date(evForm.scheduled_at).toISOString() };
      await api.post(`/streamers/${id}/events`, payload);
      toast.success("Stream scheduled.");
      setEvOpen(false);
      setEvForm({ game: "Hell Let Loose", scheduled_at: "", stream_url: "", title: "", note: "" });
      loadEvents();
    } catch (er) { toast.error(er.response?.data?.detail || "Failed"); }
  };
  const deleteEvent = async (eventId) => {
    if (!window.confirm("Delete this scheduled stream?")) return;
    try { await api.delete(`/streamers/${id}/events/${eventId}`); toast.success("Removed."); loadEvents(); }
    catch (er) { toast.error(er.response?.data?.detail || "Failed"); }
  };

  if (!s) return <div className="text-gray-500">Loading dossier…</div>;

  const canEdit = user && user !== false && (user.role === "admin" || user.email === s.email);
  const canSchedule = canEdit || user?.role === "streamer";

  const toggleLive = async () => {
    const nextLive = !s.is_live;
    const title = nextLive ? window.prompt("Optional: what's the stream title?") || "" : "";
    try {
      await api.post(`/streamers/${id}/live`, { is_live: nextLive, live_title: title });
      toast.success(nextLive ? "You're marked LIVE — the wall now shows you." : "Live status cleared.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    try {
      const payload = {
        bio: form.bio, schedule: form.schedule, avatar_url: form.avatar_url,
        channel_url: form.channel_url, game: form.game, country: form.country,
        followers: form.followers ? Number(form.followers) : undefined,
        hours_streamed: form.hours_streamed ? Number(form.hours_streamed) : undefined,
        is_live: form.is_live,
      };
      const { data } = await api.put(`/streamers/${id}`, payload);
      setS(data); setEditing(false);
      toast.success("Dossier updated.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    }
  };

  return (
    <div className="grid lg:grid-cols-12 gap-8" data-testid="dossier-root">
      <aside className="lg:col-span-5">
        <div className="sticky top-24 border border-[#232330]">
          <img
            src={s.avatar_url || "https://images.unsplash.com/photo-1560253023-3ec5d502959f?w=600"}
            alt={s.handle}
            className="w-full max-h-[70vh] object-cover"
          />
          <div className="p-5 border-t border-[#232330] bg-[#101015]">
            <div className="flex items-center gap-2 flex-wrap">
              <Radio className={`w-4 h-4 ${s.is_live ? "text-red-500 animate-pulse" : "text-[#FF007F]"}`} />
              <div className="font-heading text-2xl font-black uppercase tracking-tight">{s.handle}</div>
              {s.verified && <BadgeCheck className="w-4 h-4 text-cyan-400" />}
              {s.is_live && (
                <span className="ml-1 bg-red-600 text-white text-[10px] font-mono font-bold tracking-widest px-2 py-0.5 flex items-center gap-1">
                  <span className="inline-block w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                  LIVE
                </span>
              )}
            </div>
            <div className="text-xs text-gray-500 font-mono tracking-widest mt-1">
              {s.platform} · {s.country} · {s.game}
            </div>
            <a
              href={s.channel_url} target="_blank" rel="noreferrer"
              data-testid="dossier-channel-link"
              className="block mt-4 text-cyan-400 text-sm hover:underline font-mono truncate"
            >
              {s.channel_url}
            </a>
          </div>
        </div>
      </aside>

      <div className="lg:col-span-7 space-y-6">
        <div className="flex items-end justify-between gap-4">
          <div>
            <div className="text-cyan-400 font-mono tracking-[0.3em] text-xs mb-2">/ DOSSIER</div>
            <h1 className="font-heading text-4xl font-black tracking-tighter uppercase">{s.handle}</h1>
          </div>
          {canEdit && !editing && (
            <Button
              onClick={() => setEditing(true)}
              data-testid="edit-dossier"
              className="rounded-none bg-cyan-400 hover:bg-cyan-300 text-black uppercase tracking-widest text-xs font-bold"
            >
              <Edit3 className="w-4 h-4 mr-2" /> Edit
            </Button>
          )}
          {editing && (
            <div className="flex gap-2">
              <Button onClick={() => { setEditing(false); setForm(s); }} data-testid="cancel-dossier"
                variant="outline" className="rounded-none border-[#232330] uppercase tracking-widest text-xs">
                <X className="w-4 h-4" />
              </Button>
              <Button onClick={save} data-testid="save-dossier"
                className="rounded-none bg-[#FF007F] hover:bg-pink-400 text-white uppercase tracking-widest text-xs font-bold">
                <Save className="w-4 h-4 mr-2" /> Save
              </Button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="border border-[#232330] bg-[#101015] p-4">
            <div className="text-[10px] text-gray-500 uppercase tracking-widest font-mono">Followers</div>
            {editing ? (
              <Input type="number" value={form.followers || 0} onChange={upd("followers")}
                className="rounded-none bg-[#05050A] border-[#232330] font-mono text-xl mt-2" />
            ) : (
              <div className="font-heading font-black text-2xl text-cyan-400 mt-1">{s.followers?.toLocaleString?.() || 0}</div>
            )}
          </div>
          <div className="border border-[#232330] bg-[#101015] p-4">
            <div className="text-[10px] text-gray-500 uppercase tracking-widest font-mono">Hours Streamed</div>
            {editing ? (
              <Input type="number" value={form.hours_streamed || 0} onChange={upd("hours_streamed")}
                className="rounded-none bg-[#05050A] border-[#232330] font-mono text-xl mt-2" />
            ) : (
              <div className="font-heading font-black text-2xl text-white mt-1">{s.hours_streamed}</div>
            )}
          </div>
          <div className="border border-[#232330] bg-[#101015] p-4">
            <div className="text-[10px] text-gray-500 uppercase tracking-widest font-mono">Verified</div>
            <div className="font-heading font-black text-2xl mt-1 text-[#F4D03F]">{s.verified ? "YES" : "PENDING"}</div>
            {s.sync_source && (
              <div className="mt-3 text-[10px] font-mono tracking-widest uppercase text-gray-500">
                Auto-sync · <span className="text-cyan-400">{s.sync_source}</span>
              </div>
            )}
          </div>
        </div>

        <div className="border border-[#232330] bg-[#101015] p-6 space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-gray-400">Bio</Label>
            {editing ? (
              <Textarea data-testid="dossier-bio" rows={5} value={form.bio || ""} onChange={upd("bio")}
                className="rounded-none bg-[#05050A] border-[#232330] focus:border-cyan-400 mt-2" />
            ) : (
              <p className="text-gray-300 mt-2 leading-relaxed">{s.bio || "No dossier written yet."}</p>
            )}
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-400">Schedule</Label>
              {editing ? (
                <Input value={form.schedule || ""} onChange={upd("schedule")}
                  className="rounded-none bg-[#05050A] border-[#232330] mt-2" />
              ) : (
                <div className="text-gray-300 mt-2 font-mono">{s.schedule || "—"}</div>
              )}
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-400">Channel URL</Label>
              {editing ? (
                <Input value={form.channel_url || ""} onChange={upd("channel_url")}
                  className="rounded-none bg-[#05050A] border-[#232330] mt-2" />
              ) : (
                <div className="text-gray-300 mt-2 font-mono text-xs truncate">{s.channel_url}</div>
              )}
            </div>
          </div>
        </div>

        {canEdit && (
          <div className="border border-[#232330] bg-[#101015] p-5 space-y-3">
            <Label className="text-xs uppercase tracking-widest text-gray-400">Profile picture</Label>
            <input ref={fileRef} type="file" accept="image/*" onChange={uploadAvatar}
                   data-testid="dossier-avatar-file" className="hidden" />
            <Button type="button" onClick={() => fileRef.current?.click()}
                    data-testid="dossier-avatar-upload"
                    variant="outline"
                    className="rounded-none border-[#232330] hover:border-cyan-400 uppercase tracking-widest text-xs font-bold">
              <Upload className="w-3.5 h-3.5 mr-2" /> Upload new picture
            </Button>
            <div className="pt-3 border-t border-[#232330]">
              <Label className="text-xs uppercase tracking-widest text-gray-400">Broadcast status</Label>
              <Button
                type="button"
                onClick={toggleLive}
                data-testid="dossier-toggle-live"
                className={`mt-2 w-full rounded-none uppercase tracking-widest text-xs font-bold ${
                  s.is_live
                    ? "bg-red-600 hover:bg-red-500 text-white"
                    : "bg-[#B084FF] hover:bg-purple-400 text-black"
                }`}
              >
                {s.is_live
                  ? <><ZapOff className="w-3.5 h-3.5 mr-2" /> Mark offline</>
                  : <><Zap className="w-3.5 h-3.5 mr-2" /> Go LIVE now</>}
              </Button>
            </div>
          </div>
        )}

        <div className="border border-[#232330] bg-[#101015] p-6" data-testid="stream-events">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-[10px] font-mono tracking-widest text-[#B084FF] uppercase mb-1">/ SCHEDULED STREAMS</div>
              <div className="font-heading text-xl font-black uppercase tracking-tight">Upcoming broadcasts</div>
            </div>
            {canSchedule && (
              <Button onClick={() => setEvOpen((o) => !o)} data-testid="schedule-stream-btn"
                      className="rounded-none bg-[#B084FF] hover:bg-purple-400 text-black uppercase tracking-widest text-xs font-bold">
                <CalendarPlus className="w-3.5 h-3.5 mr-2" /> {evOpen ? "Close" : "Schedule stream"}
              </Button>
            )}
          </div>

          {evOpen && (
            <div className="mt-4 pt-4 border-t border-[#232330] grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-400">Kickoff</Label>
                <Input type="datetime-local" value={evForm.scheduled_at}
                       data-testid="ev-scheduled"
                       onChange={(e) => setEvForm({ ...evForm, scheduled_at: e.target.value })}
                       className="rounded-none bg-[#05050A] border-[#232330] mt-2" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-400">Game</Label>
                <Input value={evForm.game}
                       onChange={(e) => setEvForm({ ...evForm, game: e.target.value })}
                       className="rounded-none bg-[#05050A] border-[#232330] mt-2" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-widest text-gray-400">Stream URL</Label>
                <Input value={evForm.stream_url}
                       data-testid="ev-url"
                       onChange={(e) => setEvForm({ ...evForm, stream_url: e.target.value })}
                       placeholder="https://twitch.tv/…"
                       className="rounded-none bg-[#05050A] border-[#232330] mt-2 font-mono text-xs" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-widest text-gray-400">Title / note</Label>
                <Input value={evForm.title}
                       onChange={(e) => setEvForm({ ...evForm, title: e.target.value })}
                       placeholder="Ranked grind · squad reviews"
                       className="rounded-none bg-[#05050A] border-[#232330] mt-2" />
              </div>
              <Button onClick={submitEvent} data-testid="ev-submit"
                      className="sm:col-span-2 rounded-none bg-[#B084FF] hover:bg-purple-400 text-black uppercase tracking-widest font-bold">
                Publish stream event
              </Button>
            </div>
          )}

          <div className="mt-4 space-y-2">
            {events.length === 0 && <div className="text-xs text-gray-500 font-mono">No streams scheduled yet.</div>}
            {events.map((ev) => (
              <div key={ev.id} data-testid={`ev-${ev.id}`}
                   className="border border-[#232330] p-3 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-[10px] font-mono tracking-widest text-cyan-400 uppercase">
                    {new Date(ev.scheduled_at).toLocaleString()} · {ev.game}
                  </div>
                  {ev.title && <div className="text-sm mt-1">{ev.title}</div>}
                </div>
                <div className="flex items-center gap-2">
                  <a href={ev.stream_url} target="_blank" rel="noreferrer"
                     className="text-[10px] font-mono uppercase tracking-widest text-cyan-400 hover:text-cyan-300 flex items-center gap-1">
                    <ExternalLink className="w-3 h-3" /> Watch
                  </a>
                  {canEdit && (
                    <button onClick={() => deleteEvent(ev.id)} data-testid={`ev-del-${ev.id}`}
                            className="text-gray-500 hover:text-red-500" aria-label="Delete event">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {!canEdit && user === false && (
          <div className="border border-[#232330] p-4 text-xs text-gray-500">
            Log in as this streamer (or admin) to edit the dossier.
          </div>
        )}
      </div>
    </div>
  );
}
